/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

import {
    HttpMethod,
    HttpWrapper,
    HTTP_WRAPPER_TOKEN,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

import {
    IContentLibConfig,
    IvCenterConfiguration,
} from 'generated-types';

import { CONFIGURED_NETWORK_ITEM_TOKEN } from 'ajs/modules/network/network.tokens';
import { vCenterConfiguration } from 'object-types';
import { findWhere } from 'underscore';

const VERIFY_LOGIN_API = '/api/vimgrvcenterruntime/verify/login';
const FETCH_CONTENT_LIBRARY_API = '/api/vimgrvcenterruntime/retrieve/contentlibraries';
const CLOUD_UUID = 'cloud_uuid';

/**
 * Type of response returned by VCenterLoginVerify method.
 */
export interface IVcenterDcInfo {
    managed_object_id: string;
    name: string;
}

/**
 * Type of request params for login request.
 */
interface IVcenterLoginCredentials {
    username: string;
    password: string;
    host: string;
}

/**
 * @description
 *
 *   VcenterConfiguration MessageItem.
 *
 * @author Sarthak kapoor
 */

export type TvcenterLoginCredentials =
 Pick<IvCenterConfiguration, 'username' | 'password' | 'vcenter_url'>;

type TVcenterConfigurationPartial = Omit<IvCenterConfiguration, 'content_lib'>;

interface IVcenterConfig extends TVcenterConfigurationPartial {
    content_lib: MessageItem<IContentLibConfig>;
}

export class VCenterConfigurationConfigItem extends MessageItem<IVcenterConfig> {
    public static ajsDependencies = [
        CONFIGURED_NETWORK_ITEM_TOKEN,
        HTTP_WRAPPER_TOKEN,
        'secretStubStr',
    ];

    /**
     * Stores the list of content libraries fetched from back end.
     */
    public contentLibraries: IContentLibConfig[];

    /**
     * HttpWrapper instance to make HTTP Requests.
     */
    private httpWrapper: HttpWrapper;

    /**
     * List of datacenters.
     */
    private datacenters: IVcenterDcInfo[];

    constructor(args = {}) {
        const extendedArgs = {
            objectType: vCenterConfiguration,
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getAjsDependency_(HTTP_WRAPPER_TOKEN);

        this.httpWrapper = new HttpWrapper();
    }

    /**
     * Sets properties like username, password and vcenter_url on config.
     */
    public setVcenterLoginCredentials(loginCredentials: TvcenterLoginCredentials): void {
        const { config } = this;

        const {
            vcenter_url: vcenterUrl,
            username,
            password,
        } = loginCredentials;

        config.vcenter_url = vcenterUrl;
        config.username = username;
        config.password = password;
    }

    /**
     * Deletes the selected content lib id.
     */
    public removeSelectedContentLibId(): void {
        const { config } = this;
        const { content_lib: contentLib } = config;

        delete contentLib?.config?.id;
    }

    /**
     * Called to verify VCenter Login Credentials.
     * Also, fetches the list of datacenters.
     */
    public async vcenterLoginVerify(
        cloudId: string,
        loginCredentials?: TvcenterLoginCredentials,
    ): Promise<IVcenterDcInfo[]> {
        this.busy = true;
        this.errors = null;

        const payload = {
            ...this.loginRequestPayload(cloudId, loginCredentials),
        };

        try {
            const requestConfig = {
                url: VERIFY_LOGIN_API,
                method: HttpMethod.POST,
                data: payload,
            };

            const response = await this.httpWrapper.request(requestConfig);

            const { resource: { vcenter_dc_info: vCenterDcInfo } } = response.data;

            if (vCenterDcInfo.length === 1) {
                const { config } = this;

                config.datacenter = vCenterDcInfo[0].name;
                config.datacenter_managed_object_id = vCenterDcInfo[0].managed_object_id;
            }

            this.datacenters = [...vCenterDcInfo];

            return vCenterDcInfo;
        } catch (error) {
            return Promise.reject(error);
        } finally {
            this.busy = false;
        }
    }

    /**
     * Fetches list of VCenter Content libraries.
     */
    public async fetchContentLibraries(cloudId?: string): Promise<IContentLibConfig[]> {
        this.busy = true;
        this.errors = null;

        const { config } = this;
        const { config: contentLibConfig = {} } = config.content_lib;

        const {
            username,
            password,
            vcenter_url: host,
        } = config;

        const payload = {
            ...this.loginRequestPayload(
                cloudId,
                {
                    username,
                    password,
                    vcenter_url: host,
                },
            ),
        };

        try {
            const requestConfig = {
                url: FETCH_CONTENT_LIBRARY_API,
                method: HttpMethod.POST,
                data: payload,
            };

            const response = await this.httpWrapper.request(requestConfig);

            const { vcenter_clibs: vcenterClibs = [] } = response.data.resource;

            this.contentLibraries = [...vcenterClibs];

            /**
             * We check if the selected content lib is not present in the list of
             * content libraries fetched.
             * If not present, we reset the selected values.
             */
            if (contentLibConfig?.id) {
                const selectedContentLib = this.getSelectedContentLib();

                if (!selectedContentLib) {
                    delete contentLibConfig.id;
                }
            }

            return vcenterClibs;
        } catch (error) {
            this.errors = error.data.error;
        } finally {
            /**
             * In edit flow when all the api calls are in progress simultaneously,
             * finally of content libraries is executed even if fetch networks call is in progress.
             * This results in loader not getting displayed while
             * networks call is still in progress.
             */
            this.busy = false;
        }
    }

    /**
     * Set datacenter managed network object id based on selected datacenter.
     */
    public setDatacenterManagedNetworkObjectId(selectedDatacenter: string): void {
        const {
            config,
            datacenters,
        } = this;

        const selectedDatacenterObject = findWhere(
            datacenters, { name: selectedDatacenter },
        );

        config.datacenter_managed_object_id = selectedDatacenterObject?.managed_object_id;
    }

    /**
     * @override
     */
    protected modifyConfigDataAfterLoad(): void {
        super.modifyConfigDataAfterLoad();

        const { config } = this;

        if (!config.content_lib) {
            this.safeSetNewChildByField('content_lib');
        }
    }

    /**
     * Returns if the password field was reset or changed.
     */
    private passwordChanged(password: string): boolean {
        const secretStubStr = this.getAjsDependency_('secretStubStr');

        return password !== secretStubStr;
    }

    /**
     * Returns the payload for login and content library api calls.
     */
    private loginRequestPayload(
        cloudId: string,
        loginCredentials?: TvcenterLoginCredentials,
    ): IVcenterLoginCredentials | Record<string, string> {
        const payload: IVcenterLoginCredentials | Record<string, string> = {};

        if (!loginCredentials) {
            payload[CLOUD_UUID] = cloudId;
        } else if (this.passwordChanged(loginCredentials?.password)) {
            const {
                username,
                password,
                vcenter_url: vcenterUrl,
            } = loginCredentials;

            payload.username = username;
            payload.password = password;
            payload.host = vcenterUrl;
        } else {
            payload[CLOUD_UUID] = cloudId;
        }

        return payload;
    }

    /**
     * Returns the selected content lib object.
     */
    private getSelectedContentLib(): IContentLibConfig | undefined {
        const {
            config,
            contentLibraries,
        } = this;
        const { config: contentLibConfig = {} } = config.content_lib;

        return findWhere(contentLibraries, { id: contentLibConfig?.id });
    }
}
